<template>
  <nav v-if="pagination.totalPages > 1" class="pagination" role="navigation" aria-label="pagination">
    <div class="pagination__content">
      <!-- Prev Button -->
      <button class="pagination__btn"
              :disabled="pagination.pageIndex <= 1"
              @click.prevent="changePage(pagination.pageIndex - 1)">
        <i class="fa fa-chevron-left"></i>
        <span>Back</span>
      </button>

      <!-- Page Numbers -->
      <div class="pagination__list">
        <!-- Always show first page if there are more than 5 pages -->
        <button v-if="pagination.totalPages > 5 && pagination.pageIndex > 3"
                class="pagination__page"
                @click.prevent="changePage(1)">
          <span>1</span>
        </button>

        <!-- Left Ellipsis -->
        <button v-if="pagination.totalPages > 5 && pagination.pageIndex > 4"
                class="pagination__page no-hover">
          <span>...</span>
        </button>

        <!-- Visible Pages -->
        <button v-for="page in visiblePages"
                :key="page"
                class="pagination__page"
                :class="{ 'active': isCurrentPage(page) }"
                @click.prevent="changePage(page)">
          <span>{{ page }}</span>
        </button>

        <!-- Right Ellipsis -->
        <button v-if="pagination.totalPages > 5 && pagination.pageIndex < pagination.totalPages - 3"
                class="pagination__page no-hover">
          <span>...</span>
        </button>

        <!-- Always show last page if there are more than 5 pages -->
        <button v-if="pagination.totalPages > 5 && pagination.pageIndex < pagination.totalPages - 2"
                class="pagination__page"
                @click.prevent="changePage(pagination.totalPages)">
          <span>{{ pagination.totalPages }}</span>
        </button>
      </div>

      <label class="pagination__page-state">{{ pagination.pageIndex }} of {{ pagination.totalPages }}</label>

      <!-- Next Button -->
      <button class="pagination__btn next"
              :disabled="pagination.pageIndex >= pagination.totalPages"
              @click.prevent="changePage(pagination.pageIndex + 1)">
        <span>Next</span>
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>
    <div class="pagination__summary">
      <label>{{ displayRange }} of {{ pagination.total}}</label>
    </div>
  </nav>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

export default {
  data: function () {
    return {
      pages: [],
      startPage: 0,
      endPage: 0,
    }
  },
  computed: mapState({
    pagination: state => state.listing.pagination,
    specification: state => state.listing.specification,
    displayRange() {
      const pageIndex = Number(this.pagination.pageIndex) || 1;
      const pageSize = Number(this.pagination.pageSize) || 10;
      const total = Number(this.pagination.total) || 0;

      if (total === 0) return '0-0';

      const start = (pageIndex - 1) * pageSize + 1;
      const end = Math.min(pageIndex * pageSize, total);
      
      return `${start}-${end}`;
    },
    visiblePages() {
      let pages = [];
      for (let i = this.startPage; i <= this.endPage; i++) {
        pages.push(i);
      }
      return pages
    },
  }),
  watch: {
    pagination: {
      handler: function (newProp, oldProp) {
        // after api call, regenerate pages
        this.createStartEndPages();
      },
      deep: true,
      immediate: true
    },
  },
  methods: {
    ...mapActions('listing', { setFilter: 'setFilter' }),
    ...mapMutations('listing', { navigateToUrl: 'navigateToUrl' }),
    ...mapMutations('listing', { updateSpecification: 'updateSpecification' }),
    createStartEndPages() {
      this.startPage = this.pagination.pageIndex - 2;
      this.endPage = this.pagination.pageIndex + 2;

      if (this.startPage <= 0) {
        this.endPage -= this.startPage - 1;
        this.startPage = 1;
      }
      if (this.endPage > this.pagination.totalPages) {
        this.endPage = this.pagination.totalPages;
        if (this.endPage > 5) {
          this.startPage = this.endPage - 4;
        }
      }

      this.setPages();
    },
    isCurrentPage(page) {
      return this.pagination.pageIndex === page;
    },
    changePage(page) {
      if (page > this.pagination.totalPages) {
        page = this.pagination.totalPages;
      }

      this.updateSpecification({
        key: 'PageIndex',
        value: page - 1,
        manualSearch: false
      });
      // if (this.isSmallScreen) {
        this.setFilter();
      // }
    },
    setPages() {
      let from = this.startPage;
      let to = this.endPage;
      this.pages = [];

      while (from <= to) {
        this.pages.push(from);
        from++;
      }
    }
  },
  mounted() {
    this.createStartEndPages();
  },
}
</script>

<style scoped lang="scss">
.pagination {
  padding-top: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.125rem;

  &__content,
  &__list {
    display: flex;
    justify-content: center;
    gap: .5rem;

  }
  &__page-state {
    display: none;
    margin: auto 0;
    font-size: 1.25rem;
    @media (max-width: 991px) {
      display: block;
    }
  }
  &__btn,
  &__page {
    border-radius: .25rem;
    border: 2px solid #DDE0DC;
    background: #FFFFFF;
    color: #333333;
    height: 37px;
    font-size: 15px;
    font-weight: 700;
    display: flex;
    justify-content: center;
    padding: 0 1rem;
    gap: 1.125rem;
    transition: ease .35s;
    i {
      margin: auto 0;
    }
    span {
      margin: auto 0;
      line-height: 1;
      @media (max-width: 991px) {
        display: none;
      }
    }
    &.no-hover {
      pointer-events: none;
    }
    &:hover {
      border: 2px solid #1D8958;
    }
  }
  &__page {
    width: 37px;
    &.active {
      background: #1D8958;
      color: #ffffff;
    }
    @media (max-width: 991px) {
      display: none;
    }
  }
  &__btn {
    width: 88px;
    @media (max-width: 991px) {
      width: 37px;
    }
  }
  &__summary {
    font-size: 1.25rem;
    text-align: center;
    @media (max-width: 991px) {
      display: none;
    }
  }
}

</style>
